/*
 * Routes "preview this org template" requests from Apply Brand's own
 * Templates tab into the real Asset Library — the eye icon there doesn't
 * render its own copy of the detail view; it opens the Asset Library (dialog
 * window, or the in-pane overlay where there's no Dialog API) landing
 * directly on that template's detail card, in a view-only mode (BRD FR-18 to
 * FR-21): Use This Template / Switch Template / Keep This Template instead of
 * the Asset Library's normal Insert into Deck.
 *
 * Two halves, because the Asset Library dialog is a genuinely separate
 * browsing context (its own page, `/asset-library-dialog.html`) that shares
 * nothing with the task pane's JS realm except the same origin:
 *   - the request itself (which template, and a snapshot of the Apply Brand
 *     selection it should compare against for the view-only footer): staged
 *     in localStorage so a real Dialog window's own fresh JS module instance
 *     can read it once it mounts.
 *   - "open the library now": an in-memory listener Set — DeckorateModule
 *     (the only place holding the Asset Library launcher) and
 *     SavedTemplatesTab always share the task pane's one JS realm, so no
 *     handoff is needed for this half, only nav-bridge.ts's own pattern.
 */

const STORAGE_KEY = "aiva_deckorate_brand_template_preview_pending";

/** Staged by SavedTemplatesTab's eye icon; read by BrandTemplatesTab to drive
 *  the view-only footer (FR-20) and the "Already applied" badge (FR-18). */
export interface BrandTemplatePreviewRequest {
  /** The template Preview was opened for. */
  readonly id: string;
  /** The id currently selected in Apply Brand's Templates tab (org or Your
   *  Template), or null — the comparison basis for Use/Switch/Keep. */
  readonly selectedId: string | null;
  /** Display name for `selectedId`, for "Switching from <name> to …". */
  readonly selectedName: string | null;
  /** Id of the brand last applied to the whole presentation, or null. */
  readonly appliedId: string | null;
}

type Listener = () => void;
const listeners = new Set<Listener>();

function getStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function isPreviewRequest(value: unknown): value is BrandTemplatePreviewRequest {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === "string" &&
    (v.selectedId === null || typeof v.selectedId === "string") &&
    (v.selectedName === null || typeof v.selectedName === "string") &&
    (v.appliedId === null || typeof v.appliedId === "string")
  );
}

/** Called by SavedTemplatesTab's eye icon. Stages which template to open the
 *  detail view for (and the selection snapshot its footer compares against),
 *  then asks DeckorateModule to open the Asset Library. */
export function requestBrandTemplatePreview(request: BrandTemplatePreviewRequest): void {
  try {
    getStorage()?.setItem(STORAGE_KEY, JSON.stringify(request));
  } catch {
    /* storage full/unavailable — the Asset Library just won't land on a specific template */
  }
  for (const listener of listeners) listener();
}

/** Subscribed by DeckorateModule. Returns an unsubscribe for effect cleanup. */
export function subscribeBrandTemplatePreviewRequest(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Whether a preview is staged, without consuming it — read by
 *  AssetLibraryContent to decide whether to default to the Brand Templates
 *  tab instead of Slide Templates. */
export function hasPendingBrandTemplatePreview(): boolean {
  return getStorage()?.getItem(STORAGE_KEY) != null;
}

/** Reads and clears the staged request — called once by BrandTemplatesTab as
 *  soon as its list is ready, so a later, unrelated open doesn't replay it. */
export function consumePendingBrandTemplatePreview(): BrandTemplatePreviewRequest | null {
  const storage = getStorage();
  const raw = storage?.getItem(STORAGE_KEY) ?? null;
  storage?.removeItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isPreviewRequest(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Drops the staged request without applying it — called when the Asset
 *  Library closes, so a preview request that never got picked up (e.g. the
 *  window failed to open) can't resurface on some later, unrelated open. */
export function clearPendingBrandTemplatePreview(): void {
  getStorage()?.removeItem(STORAGE_KEY);
}
